import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled && !mobileOpen;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navTextColor = isTransparent ? 'text-background-50' : 'text-foreground-950';
  const navBg = isTransparent ? 'bg-transparent' : 'bg-background-50/95 backdrop-blur-md shadow-sm';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
      >
        <div className="flex items-center justify-between px-6 md:px-12 py-3 md:py-4">
          {/* Logo */}
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap cursor-pointer transition-colors ${
              isTransparent ? 'text-background-50' : 'text-foreground-950'
            }`}
          >
            Litbuy<span className={isTransparent ? 'text-accent-400' : 'text-primary-500'}>Spreadsheets</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4">
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                isTransparent
                  ? 'text-background-50 hover:bg-background-50/15'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  isTransparent
                    ? 'bg-background-50/15 text-background-50 hover:bg-background-50/25 backdrop-blur-sm'
                    : 'bg-background-100 text-foreground-800 hover:bg-background-200'
                }`}
              >
                Categories
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 z-50 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 whitespace-nowrap cursor-pointer transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.displayName}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide */}
            <Link
              to="/article/shopping-guide"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                isTransparent
                  ? 'text-background-50 hover:bg-background-50/15'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              Shopping Guide
            </Link>

            {/* Buy It Now */}
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${
                isTransparent
                  ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger + quick CTA */}
          <div className="md:hidden flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-primary-500 text-background-50'
              }`}
            >
              Buy Now
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
                isTransparent
                  ? 'text-background-50 hover:bg-background-50/15'
                  : 'text-foreground-800 hover:bg-background-100'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" ref={mobileRef}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-0 right-0 bottom-0 w-4/5 max-w-sm bg-background-50 shadow-2xl z-50 overflow-y-auto">
            <div className="pt-20 pb-8 px-6">
              {/* Mobile nav links */}
              <div className="flex flex-col gap-1 mb-6">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 font-medium text-sm hover:bg-background-100 cursor-pointer transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-4-line text-primary-500"></i>
                  </div>
                  Home
                </Link>
                <Link
                  to="/article/shopping-guide"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-900 font-medium text-sm hover:bg-background-100 cursor-pointer transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shopping-bag-3-line text-primary-500"></i>
                  </div>
                  Shopping Guide
                </Link>
              </div>

              {/* Mobile Categories */}
              <div className="mb-6">
                <h3 className="px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Product Categories</h3>
                <div className="flex flex-col gap-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                      </div>
                      {cat.displayName}
                    </a>
                  ))}
                </div>
              </div>

              {/* Mobile CTA */}
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-600 transition-all"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-sm"></i>
                </div>
                Shop at KA Spreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}